import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';
import clsx from 'clsx';
import { capitalize } from '@modernhealth/capitalize';
import { Icon } from '../Icon/Icon';
import { DeprecatedReactRouterLink as Link, useNavigate, isReactRouter6API, } from '../Link/DeprecatedReactRouterLink';
import { ComponentTextMedium, ComponentTextSmall, ComponentTextXSmall, } from '../Typography/Typography';
import { vitalStyles } from '../styles';
import { onSpace } from '../../interaction/keyboard';
import { pxToRem } from '../../helpers/pxToRem';
import * as styles from './DeprecatedButton.module.scss';
export var ButtonClassNames;
(function (ButtonClassNames) {
    ButtonClassNames["ButtonContainer"] = "ButtonContainer";
})(ButtonClassNames || (ButtonClassNames = {}));
export const sizes = {
    small: {
        containerSize: styles.sizeSmall,
        Text: ComponentTextXSmall,
    },
    medium: {
        containerSize: styles.sizeMedium,
        Text: ComponentTextSmall,
    },
    large: {
        containerSize: styles.sizeLarge,
        Text: ComponentTextMedium,
    },
};
/**
 * @deprecated Use {@link Button}
 */
export const DeprecatedButton = ({ title, rounded, marginTop, marginLeft, onClick, startIcon, size = 'small', variant = 'primary', fullWidth, disabled = false, testID, loading = false, titleColor, noBorder = false, iconSize = 16, ...linkProps }) => {
    const { containerSize, Text } = sizes[size];
    const baseStyle = styles.base;
    const contentStyle = styles[`content${capitalize(variant)}`];
    const containerStyle = styles[`container${capitalize(variant)}`];
    const isDisabled = loading || disabled;
    const marginTopRem = marginTop ? pxToRem(marginTop) : undefined;
    const marginLeftRem = marginLeft ? pxToRem(marginLeft) : undefined;
    return (_jsx(ButtonContainer, { ...linkProps, onClick: isDisabled ? undefined : onClick, marginTop: marginTopRem, marginLeft: marginLeftRem, disabled: isDisabled, className: clsx(linkProps.className, baseStyle, contentStyle, containerStyle, rounded && styles.roundedContainer, containerSize, fullWidth && [styles.fullWidth, vitalStyles.w100], disabled && styles.disabled, noBorder && styles.noBorder, ButtonClassNames.ButtonContainer), testID: testID, children: _jsxs(ButtonContent, { children: [startIcon && (_jsx(IconContainer, { children: _jsx(Icon, { name: startIcon, className: clsx('icon', contentStyle), size: iconSize }) })), _jsx(Text, { as: "span", className: titleColor ? undefined : clsx(contentStyle), fontColor: titleColor ? titleColor : undefined, children: title })] }) }));
};
const ButtonStyle = {
    // Buttons should be shown inline to match default/expected behavior.
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: pxToRem(2),
    justifyContent: 'center',
    borderStyle: 'solid',
    position: 'relative',
    '&:focus-visible': {
        boxShadow: `0px 0px 0px 4px ${colors.cyan20}`,
    },
};
const LinkButtonStyle = {
    textDecoration: 'none',
};
const ClickableButtonContainer = styled.button({
    ...ButtonStyle,
});
const ButtonContent = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
});
const ExternalLinkButtonContainer = styled.a({
    ...LinkButtonStyle,
    ...ButtonStyle,
});
const InternalRouteButtonContainer = styled(Link)({
    ...LinkButtonStyle,
    ...ButtonStyle,
});
const Name = 'Button';
const InternalRouteButton = props => {
    const { className, testID, marginTop, marginLeft } = props;
    const navigate = useNavigate();
    // We should correctly handle react routing without using onClick
    return (_jsx(InternalRouteButtonContainer, { className: className, role: "button", tabIndex: 0, style: {
            marginTop,
            marginLeft,
        }, onKeyUp: onSpace(() => {
            navigate(props.to, {
                replace: props.replace,
                ...(props.state ? { state: props.state } : {}),
            });
        }), "aria-disabled": false, theme: {}, to: 
        // If we are using React Router v5 and state is defined
        // we need to construct the `to` object
        !isReactRouter6API && props.state
            ? {
                pathname: props.to,
                // @ts-expect-error In v5 state was passed to `to` as a property
                state: props.state,
            }
            : props.to, ...(isReactRouter6API ? { state: props.state } : {}), replace: props.replace ?? false, "data-testid": testID, "data-component": Name, children: props.children }));
};
const ButtonContainer = props => {
    const { className, disabled, testID, marginTop, marginLeft } = props;
    // <Link /> components must define to={} so the disable state
    // must use a normal <button />
    if ('to' in props && props.to && !disabled) {
        return _jsx(InternalRouteButton, { ...props });
    }
    if ('href' in props && props.href) {
        return (_jsx(ExternalLinkButtonContainer, { className: className, role: "button", tabIndex: 0, onKeyUp: onSpace(() => {
                if (!props.href) {
                    return;
                }
                if (!props.target) {
                    window.location.href = props.href;
                }
                else {
                    window.open(props.href, props.target);
                }
            }), style: {
                marginTop,
                marginLeft,
            }, "aria-disabled": disabled, target: props.target, "data-testid": testID, "data-component": Name, ...(!disabled ? { href: props.href } : {}), children: props.children }));
    }
    if ('onClick' in props) {
        return (_jsx(ClickableButtonContainer, { className: className, style: {
                marginTop,
                marginLeft,
            }, "aria-disabled": disabled, disabled: disabled, onClick: props.onClick, "data-testid": testID, "data-component": Name, children: props.children }));
    }
    // No handlers are passed or a <Link /> button is disabled...
    return (_jsx(ClickableButtonContainer, { className: className, style: {
            marginTop,
            marginLeft,
        }, "aria-disabled": disabled, disabled: disabled, "data-testid": testID, "data-component": Name, children: props.children }));
};
const IconContainer = styled.div({
    display: 'flex',
    '.icon': {
        marginRight: pxToRem(6),
    },
});
